import { FormControl, FormGroup } from '@angular/forms';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';

export interface DiscountCodesFormShape {
  discountCodesEnabled: FormControl<boolean>;
  giftCardCodesEnabled: FormControl<boolean>;
  title: FormControl<string>;
  headerLabel: FormControl<string>;
  buttonLabel: FormControl<string>;
  // Design fields are nullable: null means "inherit from global"; a value means the merchant
  // has explicitly overridden it for this widget. Resolution happens at render time
  // (storefront uses `widget.design.X ?? global.buttonDesign.X`).
  design: FormGroup<{
    backgroundColor: FormControl<string | null>;
    textColor: FormControl<string | null>;
    hoverColor: FormControl<string | null>;
    cornerRadius: FormControl<number | null>;
  }>;
  invalidMessage: FormControl<string>;
  customCode: FormGroup<CustomCodeFormShape>;
}

const DEFAULTS = {
  discountCodesEnabled: true,
  giftCardCodesEnabled: true,
  title: 'Enter your discount code or gift card',
  headerLabel: 'Discount Code',
  buttonLabel: 'Apply',
  invalidMessage: '{entered_code} is not valid for this cart',
};

export const DISCOUNT_CODES_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  {
    reference: '{{discountCodesEnabled}}',
    type: 'Boolean',
    description: 'True when discount codes are accepted in the admin',
  },
  {
    reference: '{{giftCardCodesEnabled}}',
    type: 'Boolean',
    description: 'True when gift card codes are accepted in the admin',
  },
];

export const DISCOUNT_CODES_STARTER_TEMPLATE = `<div
  style="padding: 12px 20px;"
>
  <!-- The input, apply button, applied codes pills, and error message
       all render inside this slot — keep it in your template. -->
  <div data-pdq-slot="codes-widget"></div>
</div>`;

export function createDiscountCodesFormGroup(
  initialSettings?: Record<string, unknown> | null,
): FormGroup<DiscountCodesFormShape> {
  const formGroup = new FormGroup<DiscountCodesFormShape>({
    discountCodesEnabled: new FormControl(DEFAULTS.discountCodesEnabled, { nonNullable: true }),
    giftCardCodesEnabled: new FormControl(DEFAULTS.giftCardCodesEnabled, { nonNullable: true }),
    title: new FormControl(DEFAULTS.title, { nonNullable: true }),
    headerLabel: new FormControl(DEFAULTS.headerLabel, { nonNullable: true }),
    buttonLabel: new FormControl(DEFAULTS.buttonLabel, { nonNullable: true }),
    design: new FormGroup({
      backgroundColor: new FormControl<string | null>(null),
      textColor: new FormControl<string | null>(null),
      hoverColor: new FormControl<string | null>(null),
      cornerRadius: new FormControl<number | null>(null),
    }),
    invalidMessage: new FormControl(DEFAULTS.invalidMessage, { nonNullable: true }),
    customCode: createCustomCodeFormGroup(undefined, DISCOUNT_CODES_STARTER_TEMPLATE),
  });

  if (initialSettings) {
    formGroup.patchValue(initialSettings, { emitEvent: false });
  }

  return formGroup;
}
